import React from 'react';
import _ from 'lodash';
import { ButtonSelect } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import UpSelect from 'Components/Inputs/UpSelect';
import { StaticUserMultiSelect } from 'App/components/Inputs/Selects/StaticUserSelect/StaticUserSelect';
import { useSelf, useUsers } from 'App/components/hooks/appHooks';
import { BasicUserWithPermissions } from 'App/resources/Model/User';
import { FilterConfig, FilterConfigRaw } from 'App/babel/filterConfigs/FilterConfig';
import { dateOf, newFilter, PersonalEmailValue } from 'App/babel/angular/attributes/filter/personalEmailFilter';
import { datepicker as Datepicker } from '../date';

type PersonalEmailPageProps = {
	filter: FilterConfigRaw & { value?: PersonalEmailValue; inactive?: boolean };
	onChange(filterConfig: FilterConfig, action: { action: string }): void;
};

const ALL = 'all';

// Non-admins filter by their own mail only until mail conditions carry mail auth (WEB-12903);
// the API enforces the same (mailAuth).
const useMailSenders = () => {
	const self = useSelf();
	const readable = useUsers('activity');
	if (self?.administrator || !self) {
		return readable;
	}
	// Self carries what the picker needs (id, name) but not a directory entry's flags.
	return [{ ...self, active: true, ghost: false, apiUser: false } as unknown as BasicUserWithPermissions];
};

const PersonalEmailPage = ({ filter, onChange }: PersonalEmailPageProps) => {
	const { t } = useTranslation();
	const value = (filter.value ?? filter.generate?.().value) as PersonalEmailValue;
	const inactive = filter.value ? !!filter.inactive : true;
	const date = dateOf(value.Date.value);

	const update = (updates: { path: string; value: unknown }[]) => {
		let newFilter = _.cloneDeep(filter);
		if (!filter.value) {
			newFilter = Object.assign(newFilter, newFilter.generate?.());
		}
		updates.forEach(({ path, value }) => _.set(newFilter, path, value));
		onChange(newFilter as FilterConfig, { action: 'add' });
	};

	// Keeps a range picked here custom even once both ends are cleared, since a range ui-3
	// saved carries no preset of its own.
	const onDateChange = (side: 'start' | 'end') => (picked: Date | null) =>
		update([
			{ path: 'value.Date.value.preset', value: 'custom' },
			{ path: `value.Date.value.${side}`, value: picked }
		]);

	const onHasChange = (has: string | boolean) =>
		has === ALL
			? update([
					{ path: 'inactive', value: true },
					{ path: 'value', value: newFilter().value }
			  ])
			: update([
					{ path: 'inactive', value: false },
					{ path: 'value.has', value: has }
			  ]);

	// A view saved in ui-3 can carry a preset this list does not offer; keep it selectable.
	const presetIds = [...new Set([...(filter.presets ?? []), date.preset])];
	const presets = presetIds.map(preset => ({ id: preset, name: t('date.' + preset) }));
	// Saved senders may since have been deactivated; they still filter, so they must still show.
	const knownUsers = [
		...useUsers('active', true, 'all'),
		...useUsers('inactive', true, 'all'),
		...useUsers('deleted', true, 'all')
	];
	const senders = knownUsers
		.filter(user => value.User.value.includes(user.id))
		.map(user => ({ ...user, title: user.name }));

	return (
		<div className="history-filter">
			<div className="title">{t('filter.personalEmail')}</div>
			<div className="history-filter-row extra-thin-row">
				<ButtonSelect
					className="flex-btn-group"
					color="bright-blue"
					onChange={onHasChange}
					options={[
						{ title: t('default.all'), value: ALL },
						{ title: t('advancedSearch.has'), value: true },
						{ title: t('advancedSearch.hasNot'), value: false }
					]}
					value={inactive ? ALL : value.has}
				/>
			</div>
			<div className="history-filter-row">
				<label className="secondary-title">{t('mail.sentDateFilter')}</label>
				<div className={'datepicker-group' + (date.preset === 'custom' ? ' datepicker-custom-visible' : '')}>
					<div className="datepicker-select-wrap">
						<UpSelect
							disabled={inactive}
							data={presets}
							required={true}
							multiple={false}
							className="datepicker-select"
							key={date.preset}
							defaultValue={date.preset}
							onChange={(event: { target: { value: string } }) =>
								update([
									{ path: 'value.Date.value.preset', value: event.target.value },
									{ path: 'value.Date.value.start', value: null },
									{ path: 'value.Date.value.end', value: null }
								])
							}
						/>
					</div>
					{date.preset === 'custom' ? (
						<div className="datepicker-wrap">
							<div id="datepicker-container">
								<Datepicker
									disabled={inactive}
									placeholder={t('date.from')}
									name="startDate"
									className="form-control"
									value={date.start}
									onChange={onDateChange('start')}
								/>
								<Datepicker
									disabled={inactive}
									placeholder={t('date.to')}
									name="endDate"
									className="form-control"
									value={date.end}
									onChange={onDateChange('end')}
								/>
							</div>
						</div>
					) : null}
				</div>
			</div>
			<div className="history-filter-row">
				<label className="secondary-title">{t('mail.sentBy')}</label>
				<StaticUserMultiSelect
					anchor="#list-filters-wrap"
					userGetter={useMailSenders}
					disabled={inactive}
					value={senders}
					onChange={selected => update([{ path: 'value.User.value', value: selected.map(user => user.id) }])}
				/>
			</div>
		</div>
	);
};

export default PersonalEmailPage;
